import React, { Component } from 'react';
import { bool, func, number, string } from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { ensureListing } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import { NamedLink, ResponsiveImage, IconSpinner } from '..';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';

import css from './CartCard.module.css';
import { useConfiguration } from '../../context/configurationContext';
import { formatMoney } from '../../util/currency';
import { conditionLabel } from '../../util/listingCondition';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

class ListingImage extends Component {
  render() {
    return <ResponsiveImage {...this.props} />;
  }
}
const LazyImage = lazyLoadWithDimensions(ListingImage, { loadAfterInitialRendering: 3000 });

/** The stepper's minus, or its plus. */
const StepIcon = ({ plus = false }) => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
    <path d="M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    {plus ? (
      <path d="M8 3v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    ) : null}
  </svg>
);

/** A text button under the item ("Save for later", "Remove"): a spinner inside while it runs. */
const ItemAction = ({ label, ariaLabel, inProgress, disabled, onClick }) => (
  <button
    type="button"
    className={css.action}
    onClick={onClick}
    disabled={disabled || inProgress}
    aria-busy={inProgress || undefined}
    aria-label={ariaLabel}
  >
    {inProgress ? <IconSpinner rootClassName={css.actionSpinner} /> : null}
    {label}
  </button>
);

/**
 * One listing in the cart. The quantity, the remove or save in progress and a failed save
 * come from the cart in the store (CartPage.duck); "+", "-", "Save for later" and "Remove"
 * go back to it through onChangeQuantity, onSaveForLater and onRemove.
 *
 * A listing that can't be bought (purchasable false: sold out or closed) shows as no longer
 * available, with only "Remove". The phone and desktop layouts are both CSS.
 */
export const CartCardComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    quantity,
    purchasable,
    titleId,
    removeInProgress,
    saveForLaterInProgress,
    saveFailed,
    onChangeQuantity,
    onRemove,
    onSaveForLater,
  } = props;
  const config = useConfiguration();
  const classes = classNames(rootClassName || css.root, className, {
    [css.soldOut]: !purchasable,
  });
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const firstImage = currentListing.images?.[0] || null;
  const { variantPrefix = 'listing-card' } = config.layout?.listingImage || {};
  const allVariants = firstImage?.attributes?.variants
    ? Object.keys(firstImage.attributes.variants)
    : [];
  const prefixVariants = allVariants.filter(k => k.startsWith(variantPrefix));
  const scaledVariants = allVariants.filter(k => k.startsWith('scaled-'));
  // The card variants when there are any, else the scaled ones, else whatever there is
  const variants =
    prefixVariants.length > 0
      ? prefixVariants
      : scaledVariants.length > 0
      ? scaledVariants
      : allVariants;

  const currentQuantity = quantity || 1;
  // A sold-out item shows its unit price, struck through
  const shownQuantity = purchasable ? currentQuantity : 1;
  const formattedPrice = price
    ? formatMoney(intl, new Money(price.amount * shownQuantity, price.currency))
    : null;
  const conditionText = conditionLabel(intl, publicData?.condition);
  const availableStock = currentListing.currentStock?.attributes?.quantity || 0;
  const busy = removeInProgress || saveForLaterInProgress;

  const handleIncrease = () => {
    if (currentQuantity < availableStock) onChangeQuantity(currentQuantity + 1);
  };
  const handleDecrease = () => {
    if (currentQuantity > 1) onChangeQuantity(currentQuantity - 1);
  };

  const titleText = richText(title, {
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });
  const availability = (
    <FormattedMessage id="CartCard.available" values={{ count: availableStock }} />
  );

  return (
    <div className={classes}>
      {/* The title link is the item's one tab stop; the image repeats it for pointers */}
      <NamedLink
        className={css.imageLink}
        name="ListingPage"
        params={{ id, slug }}
        tabIndex={-1}
        aria-hidden="true"
      >
        <LazyImage
          rootClassName={css.image}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </NamedLink>

      <div className={css.titleArea}>
        {purchasable ? (
          <NamedLink id={titleId} className={css.title} name="ListingPage" params={{ id, slug }}>
            {titleText}
          </NamedLink>
        ) : (
          <>
            <span id={titleId} className={css.titleSoldOut} tabIndex={-1}>
              {titleText}
            </span>
            <span className={css.soldOutBadge}>
              <FormattedMessage id="CartCard.soldOutBadge" />
            </span>
          </>
        )}
      </div>

      <div className={css.price}>
        {formattedPrice ? (
          purchasable ? (
            formattedPrice
          ) : (
            <s className={css.priceStruck}>{formattedPrice}</s>
          )
        ) : null}
      </div>

      <p className={css.meta}>
        {purchasable ? (
          <>
            {conditionText ? (
              <span className={css.condition}>
                <FormattedMessage id="CartCard.condition" values={{ condition: conditionText }} />
              </span>
            ) : null}
            <span className={css.metaAvailability}>{availability}</span>
          </>
        ) : (
          <FormattedMessage id="CartCard.soldOutText" />
        )}
      </p>

      {purchasable ? (
        <div className={css.quantitySection}>
          <div className={css.stepper}>
            <button
              className={css.stepperButton}
              onClick={handleDecrease}
              disabled={currentQuantity <= 1 || busy}
              type="button"
              aria-label={intl.formatMessage({ id: 'CartCard.decrease' }, { title })}
            >
              <StepIcon />
            </button>
            <span
              className={css.stepperValue}
              role="status"
              aria-live="polite"
              aria-label={intl.formatMessage({ id: 'CartCard.quantityLabel' }, { title })}
            >
              {currentQuantity}
            </span>
            <button
              className={css.stepperButton}
              onClick={handleIncrease}
              disabled={currentQuantity >= availableStock || busy}
              type="button"
              aria-label={intl.formatMessage({ id: 'CartCard.increase' }, { title })}
            >
              <StepIcon plus />
            </button>
          </div>
          <span className={css.stockAvailable}>{availability}</span>
        </div>
      ) : null}

      <div className={css.actions}>
        {purchasable ? (
          <ItemAction
            label={<FormattedMessage id="CartCard.saveForLater" />}
            ariaLabel={intl.formatMessage({ id: 'CartCard.saveForLaterLabel' }, { title })}
            inProgress={saveForLaterInProgress}
            disabled={removeInProgress}
            onClick={onSaveForLater}
          />
        ) : null}
        <ItemAction
          label={<FormattedMessage id="CartCard.remove" />}
          ariaLabel={intl.formatMessage({ id: 'CartCard.removeLabel' }, { title })}
          inProgress={removeInProgress && !saveForLaterInProgress}
          disabled={saveForLaterInProgress}
          onClick={onRemove}
        />
      </div>

      {saveFailed ? (
        <p className={css.saveError} role="alert">
          <FormattedMessage id="CartCard.saveFailed" />
        </p>
      ) : null}
    </div>
  );
};

CartCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  quantity: 1,
  purchasable: true,
  titleId: null,
  removeInProgress: false,
  saveForLaterInProgress: false,
  saveFailed: false,
  onSaveForLater: () => null,
};

CartCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  // Responsive image sizes hint
  renderSizes: string,
  // The cart in the store
  quantity: number,
  purchasable: bool,
  // The title's id, so the page can move focus to it
  titleId: string,
  removeInProgress: bool,
  saveForLaterInProgress: bool,
  saveFailed: bool,
  onChangeQuantity: func.isRequired,
  onRemove: func.isRequired,
  onSaveForLater: func,
};

export default injectIntl(CartCardComponent);
